import debounce from 'lodash/debounce';
import { useCallback, useMemo } from 'react';

import { useGetAlbumVideoSuggestedUserMentionsLazy } from '@vimeo/gctl-api/hooks/useAlbumVideoSuggestedUserMentions';
import { useGetVideoSuggestedUserMentionsLazy } from '@vimeo/gctl-api/hooks/useVideoSuggestedUserMentions';

import {
  buildUserMentionSuggestion,
  getReviewPasswordHashFromCookie,
  getShowcasePasswordHashFromCookie,
} from '../../utils/helper';

/**
 * A hook to handle search ,load mention list suggestions
 */
export const useUserMentionSuggestions = (
  clipRequestId: string,
  perPage: number,
  page: number,
  showcaseId?: number,
  reviewId?: string,
) => {
  const password = getReviewPasswordHashFromCookie(reviewId);

  const [searchMentionSuggestions, mentionSuggestionsState] =
    useGetVideoSuggestedUserMentionsLazy();

  const [searchAlbumMentionSuggestions, albumMentionSuggestionsState] =
    useGetAlbumVideoSuggestedUserMentionsLazy();

  const { loading: loadingMentionSuggestion, data: mentionSuggestions } = showcaseId
    ? albumMentionSuggestionsState
    : mentionSuggestionsState;

  const getMentionSuggestionItems = useCallback(
    (queryString: string, filter: 'collaborator' | 'viewer' = 'viewer', teamOwnerId?: number) => {
      if (clipRequestId) {
        searchMentionSuggestions({
          where: {
            videoId: clipRequestId as unknown as number,
          },
          query: {
            query: queryString,
            page,
            perPage,
            filter,
            teamOwnerId,
            reviewId,
            password,
          },
          select: [],
        });
      }
    },
    [searchMentionSuggestions, clipRequestId, perPage, page],
  );

  const getAlbumMentionSuggestionItems = useCallback(
    (queryString: string, filter: 'collaborator' | 'viewer' = 'viewer', teamOwnerId?: number) => {
      if (clipRequestId && showcaseId) {
        const showcasePassword = getShowcasePasswordHashFromCookie(showcaseId);
        searchAlbumMentionSuggestions({
          where: {
            albumId: showcaseId,
            videoId: clipRequestId as unknown as number,
          },
          query: {
            query: queryString,
            page,
            perPage,
            filter,
            teamOwnerId,
            ...(showcasePassword ? { password: showcasePassword } : {}),
          },
          select: [],
        });
      }
    },
    [clipRequestId, showcaseId, searchAlbumMentionSuggestions, page, perPage],
  );

  const getMentionSuggestionItemsDebounced = useMemo(() => {
    if (showcaseId) return debounce(getAlbumMentionSuggestionItems, 500);
    return debounce(getMentionSuggestionItems, 500);
  }, [getAlbumMentionSuggestionItems, getMentionSuggestionItems, showcaseId]);

  const mentionSuggestionList = useMemo(
    () =>
      mentionSuggestions?.data
        ?.filter((suggestion) => suggestion.uri)
        .map((suggestion) => buildUserMentionSuggestion(suggestion)) || [],
    [mentionSuggestions],
  );

  return {
    loadingMentionSuggestion,
    mentionSuggestionList,
    getMentionSuggestionItemsDebounced,
  };
};
